import type * as types from '../types';

import { logger } from '../../../logger';

/**
 * @see {@link types.IModifierChild}
 */
export class ModifierChild implements types.IModifierChild {
  private readonly _native: Omit<types.MuiModifier, 'name' | 'settings'>;
  public readonly name: string;
  public readonly parent: types.IModifierConfiguration;
  public readonly settings: types.ChildModifierSettings;

  constructor(
    name: string,
    parent: types.IModifierConfiguration,
    native: Omit<types.MuiModifier, 'name' | 'settings'>,
    settings: types.ChildModifierSettings,
  ) {
    this.name = name;
    this.parent = parent;
    this._native = native;
    this.settings = settings;
  }

  public static create(
    name: string,
    parent: types.IModifierConfiguration,
    {
      settings,
      ...native
    }: { readonly settings: types.ChildModifierSettings } & Omit<types.MuiModifier, 'name'>,
  ): ModifierChild {
    return new ModifierChild(name, parent, native, settings);
  }

  private getLogInfoOnChildConflict(
    conflictingModifier: types.MuiModifier,
  ): [string, Record<string, unknown>] {
    const base =
      `When applying the custom modifier with name '${this.name}', a modifier with name ` +
      `${this.name} was encountered and conflicts with the modifier(s) applied by the ` +
      'custom modifier itself.';
    if (this.settings.messageOnConflict !== undefined) {
      return [
        [base, 'Additional information:', this.settings.messageOnConflict].join(' '),
        { conflictingModifier },
      ];
    }
    return [base, { conflictingModifier }];
  }

  public logConflict(conflictingModifier: types.MuiModifier): void {
    const [message, context] = this.getLogInfoOnChildConflict(conflictingModifier);
    this.logWarning(message, context);
  }

  public logWarning(message: string, context?: Record<string, unknown>): void {
    this.parent.logWarning(`[Child ${this.name}]: ${message}`, {
      ...context,
      ...this.logContext,
    });
  }

  private get logContext() {
    return { name: this.name, options: this.options, settings: this.settings };
  }

  public get native(): types.MuiModifier {
    return {
      ...this._native,
      enabled: this._native.enabled ?? true,
      name: this.name,
    };
  }

  public get options() {
    return this._native.options;
  }
}

/**
 * @see {@link types.IModifierConfiguration}
 */
export abstract class ModifierConfiguration<
  N extends types.CustomModifierName = types.CustomModifierName,
  Defaults extends Partial<types.CustomModifierOptions<N>> = Partial<
    types.CustomModifierOptions<N>
  >,
> implements types.IModifierConfiguration<N> {
  public abstract defaultOptions: Defaults;
  public abstract name: N;

  protected abstract children(
    options: types.WithDefaults<types.CustomModifierOptions<N>, Defaults>,
    props: types.NullableButRequiredCustomModifierApplicableProps,
  ): ModifierChild[];

  public getChildren(
    modifier: types.CustomModifier<N>,
    props: types.NullableButRequiredCustomModifierApplicableProps,
  ): ModifierChild[] {
    const options = { ...this.defaultOptions, ...modifier.options } as Record<
      string,
      unknown
    > as types.WithDefaults<types.CustomModifierOptions<N>, Defaults>;
    return this.children(options, props);
  }

  public logPropCollisions(_: types.NullableButRequiredCustomModifierApplicableProps): void {
    /* Not implemented by default.  This method is not necessary for all custom modifiers and
       can be opted into by subclasses as needed. */
  }

  public logWarning(message: string, context?: Record<string, unknown>): void {
    logger.warn(`Custom Popper Modifier [${this.name}]: ${message}`, {
      ...context,
      modifierName: this.name,
    });
  }
}
